import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NonIntegratedSourcingChannel,
  SourcingService,
} from 'api-clients/monolith';
import _range from 'lodash/range';
import { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import EditIcon from '@/assets/icons/edit.svg';
import EmptyChannels from '@/assets/icons/empty-channels.svg';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { TrashIcon } from '@/components/icons/trash';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

import { NonIntegratedChannelsTableHeader } from './table-header';

interface NonIntegratedChannelsTableProps {
  onManageChannel: (channelId: string) => void;
  mappedChannels: NonIntegratedSourcingChannel[];
}

export function NonIntegratedChannelsTable({
  onManageChannel,
  mappedChannels,
}: NonIntegratedChannelsTableProps) {
  const gridTemplateColumns = `1fr 1fr 2fr 132px`;
  const intl = useIntl();
  const { confirm } = useConfirmation();
  const { notifySuccess, notifyError } = useToaster();
  const queryClient = useQueryClient();

  const { mutate: deleteChannel } = useMutation({
    mutationFn: (channelId: string) =>
      SourcingService.deleteInternalApiSourcingSourcingChannels(channelId),
    onSuccess: () => {
      // Invalidate non-integrated channels and UTM mapping queries to update the UI
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.settings.channels.nonIntegrated],
      });
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.settings.channels.utmMapping],
      });

      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Channel deleted successfully',
          description: 'Success message for successful channel deletion',
        }),
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to delete channel',
          description: 'Error message for failed channel deletion',
        }),
      });
    },
  });

  const handleDeleteChannel = async (
    channelId: string,
    channelName: string,
  ) => {
    const confirmTitle = intl.formatMessage(
      {
        defaultMessage: 'Delete {channelName}',
        description:
          'Dialog title when deleting a non-integrated channel by name',
      },
      {
        channelName,
      },
    );

    const isDeleteConfirmed = await confirm({
      title: confirmTitle,
      description: (
        <span>
          <FormattedMessage
            defaultMessage="Deleting this channel will remove it from your configuration and unassign its mapped UTM sources."
            description="Delete confirmation: first paragraph about UTM mapping removal"
          />
          <br />
          <br />
          <FormattedMessage
            defaultMessage="Note: Applicants previously attributed to this channel will remain in reports. You can recreate the channel and remap sources at any time."
            description="Delete confirmation: second paragraph on historical report data"
          />
        </span>
      ),
      confirmLabel: confirmTitle,
      confirmVariant: 'destructive',
    });

    if (isDeleteConfirmed) {
      deleteChannel(channelId);
    }
  };

  if (mappedChannels.length === 0)
    return (
      <EmptyState
        className="w-full"
        icon={EmptyChannels}
        header={intl.formatMessage({
          defaultMessage: 'No channels configured yet!',
          description: 'Empty state for non-integrated (UTM-only) channel list',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Create your first non-integrated channel to group UTM sources and track their performance.',
          description:
            'Explains how non-integrated channels group UTM traffic for reporting',
        })}
      />
    );

  return (
    <>
      {/* Table headers */}
      <NonIntegratedChannelsTableHeader
        gridTemplateColumns={gridTemplateColumns}
      />

      {/* Table rows */}
      {mappedChannels.map(channel => (
        <Fragment key={channel.channel_id}>
          <div
            className="grid items-center gap-x-4 px-4 py-3"
            style={{ gridTemplateColumns }}
          >
            {/* Channel name */}
            <div className="flex items-center gap-2">
              <ChannelIcon
                channelSlug={channel.channel_slug}
                channelName={channel.channel_name}
                variant="small"
                size="sm"
              />
              <Typography variant="subtitle1">
                {channel.channel_name}
              </Typography>
            </div>

            {/* Applicant count */}
            <Typography variant="body1">
              <FormattedMessage
                defaultMessage="{count} applicants"
                description="Applicant count"
                values={{ count: channel.applicant_count }}
              />
            </Typography>

            {/* UTM sources */}
            <div className="flex flex-wrap gap-2">
              {channel.utm_sources.map(utmSource => (
                <Tag
                  key={utmSource.mapping_key_id}
                  text={intl.formatMessage(
                    {
                      defaultMessage: '{value} • {applicant_count} applicants',
                      description: 'UTM source value and applicant count',
                    },
                    {
                      value: utmSource.value,
                      applicant_count: utmSource.applicant_count,
                    },
                  )}
                  className="bg-fx-neutral-100"
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center">
              <Button
                variant="ghost"
                type="button"
                onClick={() => onManageChannel(channel.channel_id)}
              >
                <img src={EditIcon} alt="Edit" />
                <FormattedMessage
                  defaultMessage="Manage"
                  description="Button to manage the channel"
                />
              </Button>

              <Button
                variant="ghost"
                type="button"
                aria-label={intl.formatMessage(
                  {
                    defaultMessage: 'Delete {channelName}',
                    description:
                      'Dialog title when deleting a non-integrated channel by name',
                  },
                  { channelName: channel.channel_name },
                )}
                onClick={() => {
                  void handleDeleteChannel(
                    channel.channel_id,
                    channel.channel_name,
                  );
                }}
              >
                <TrashIcon className="text-fx-rose" />
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="bg-fx-neutral-200 h-px w-full" />
        </Fragment>
      ))}
    </>
  );
}

NonIntegratedChannelsTable.Skeleton =
  function NonIntegratedChannelsTableSkeleton() {
    return (
      <>
        <NonIntegratedChannelsTableHeader gridTemplateColumns="1fr 1fr 2fr 132px" />

        {_range(3).map(index => (
          <Fragment key={index}>
            <div
              className="grid h-15 items-center gap-x-4 px-4 py-3"
              style={{ gridTemplateColumns: '1fr 1fr 2fr 132px' }}
            >
              {/* Channel name */}
              <div className="flex items-center gap-1">
                <Skeleton className="h-5 w-5 rounded-full" />
                <Skeleton className="h-5 w-24" />
              </div>

              {/* Applicant count */}
              <Skeleton className="h-4 w-28" />

              {/* UTM sources */}
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-6 w-32 !rounded-full" />
                <Skeleton className="h-6 w-50 !rounded-full" />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </div>
            </div>

            <div className="bg-fx-neutral-200 h-px w-full" />
          </Fragment>
        ))}
      </>
    );
  };
